import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  MagnifyingGlassIcon,
  MusicNotesIcon,
  PauseIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
  SpeakerHighIcon,
  SpeakerLowIcon,
  SpeakerNoneIcon,
  SpeakerSlashIcon,
  XIcon,
} from '@phosphor-icons/react';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake } from '../../../data/kioskRefresh';
import { SpotifyLogo, YouTubeLogo } from '../../../components/ThirdPartyLogos';
import { MusicBrowserModal } from './MusicBrowserModal';

// Kitchen-kiosk media panel (music + YouTube video). Lives in the launcher slot
// under the clock and swaps between two states:
//   - idle  → the 2-up YouTube / Spotify launcher buttons (an Apple Music
//             button used to sit third, but was removed 2026-09 as it was
//             never wired to anything Apple-specific — it opened the same MA
//             picker as Spotify. Re-add via ThirdPartyLogos' AppleMusicLogo
//             when a real Apple Music flow exists.)
//   - active → a compact now-playing card (art, title/artist, transport) with
//              an X that stops playback and brings the launchers back
//
// YouTube launches a fullscreen youtube.com/tv cast target on the Pi (see
// openYouTube + api/kiosk/_youtube.ts); Spotify/Apple Music play through Music
// Assistant. The now-playing card tracks the MA player only.
//
// Tapping Spotify (idle) or the search button (now-playing)
// opens the unified <MusicBrowserModal/> to pick a playlist, see recently
// played, or search — picking a result plays it on the kiosk player.
//
// Backed by Home Assistant via the shared /api/ha proxy, which fronts a single
// Music Assistant output player wired to the display's built-in Hi-Fi
// speakers. Spotify and Apple Music are *providers* inside Music Assistant —
// they play *through* this one media_player entity — so the panel is
// provider-agnostic and always tracks the same entity.
//
// ⚙️ CONFIG — fill these in once Music Assistant is set up on HA:

// The Music Assistant player the kiosk controls. Points at the living room
// TCL Roku TV (model 7105X), which MA drives over AirPlay 2 — so kiosk-started
// Spotify / Apple Music plays out the living room rather than the kitchen Pi's
// own speakers. Note AirPlay audio to a Roku wakes the TV and shows a
// now-playing screen; that's expected.
//
// To send audio back to the kitchen display instead, switch this to
// `media_player.kitchendisplay` — the kiosk Pi's squeezelite player (slimproto
// provider on port 3483; outputs to the display's HDMI Hi-Fi via
// plughw:CARD=vc4hdmi1,DEV=0, see docs/kiosk-pi-setup.md). Other MA players on
// the network: `media_player.basement` (HomePod), `media_player.dktb` (Mac).
export const KIOSK_PLAYER_ENTITY = 'media_player.living_room';

// The Music Assistant config entry id — required by the music_assistant.search
// / get_library browse actions. Grab it from HA → Developer Tools → Actions →
// `music_assistant.search` → fill the GUI dropdown → switch to YAML → copy the
// `config_entry_id` value. (Until set, the browser modal shows a config error.)
export const MA_CONFIG_ENTRY_ID = '01KTCEDJSNDNEJD1PEYNB93R3C';

const REFRESH_MS = 3 * 1000;
const POST_REFRESH_DELAY_MS = 800;
// Long-press duration that swaps the speaker button's tap (mute) gesture for
// the volume-slider popover.
const VOLUME_HOLD_MS = 400;
// Pixels of vertical drag that span the full 0–100 volume range — matches the
// popover slider's visible track height so the hold-and-drag gesture tracks the
// slider 1:1.
const VOLUME_TRACK_PX = 144;
// Drag during volume changes spams HA; coalesce the volume_set call so only the
// latest value is sent shortly after the finger settles.
const VOLUME_DEBOUNCE_MS = 120;
// A now-playing session left paused this long is treated as abandoned: stop it
// and fall back to the launcher buttons.
const PAUSE_RESET_MS = 15 * 60 * 1000;
// States in which a playback session exists (so we show now-playing rather
// than the launchers). Anything else — idle / off / stopped / unavailable —
// means nothing is going, so the launchers come back.
const ACTIVE_STATES = ['playing', 'paused', 'buffering'];

interface MediaPlayerAttributes {
  friendly_name?: string;
  media_title?: string;
  media_artist?: string;
  media_album_name?: string;
  media_content_id?: string;
  app_name?: string;
  volume_level?: number;
  is_volume_muted?: boolean;
  entity_picture?: string;
  media_image_url?: string;
  // Playback position, in seconds, captured at media_position_updated_at; with
  // media_duration these drive the donut progress ring around the cover art.
  media_position?: number;
  media_duration?: number;
  media_position_updated_at?: string; // ISO timestamp
}

interface EntityState {
  entity_id: string;
  state: string; // 'playing' | 'paused' | 'idle' | 'off' | 'unavailable' | 'buffering'
  attributes: MediaPlayerAttributes;
}

interface HaResponse {
  states: Record<string, EntityState>;
  stale: boolean;
}

interface MediaPanelProps {
  className?: string;
}

export function MediaPanel({ className }: MediaPanelProps = {}) {
  const [data, setData] = useState<HaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Optimistic play/pause override, cleared once a poll confirms the server
  // agrees — keeps the toggle instant despite HA's eventual consistency.
  const [optimisticPlaying, setOptimisticPlaying] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  // Set when the user hits X: forces the launchers back immediately (before the
  // stop propagates through a poll). Cleared once the server confirms the
  // player is no longer active, or when something new starts.
  const [dismissed, setDismissed] = useState(false);
  // Music-picker modal.
  const [browserOpen, setBrowserOpen] = useState(false);
  // Volume (0–100) with an optimistic override cleared once a poll agrees —
  // same pattern as play/pause.
  const [optimisticVolume, setOptimisticVolume] = useState<number | null>(null);
  // This player exposes volume_set but NOT volume_mute (calling volume_mute on
  // it 500s), so we "mute" by dropping volume to 0 and restoring it on unmute.
  // `muted` is a client-side affordance — there's no server mute state to sync
  // from — and the pre-mute volume is remembered so unmute returns to it.
  const [muted, setMuted] = useState(false);
  const preMuteVolRef = useRef(50);
  // Volume-slider popover anchored above the speaker button (long-press opens).
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const speakerBtnRef = useRef<HTMLButtonElement | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const heldRef = useRef(false);
  // While a hold-and-drag is live: the pointer Y and volume at the moment the
  // popover opened, so subsequent moves map travel → volume delta.
  const dragRef = useRef<{ startY: number; startVol: number } | null>(null);
  const volumeTimerRef = useRef<number | null>(null);
  const alive = useRef(true);

  // Album-art object URL. Fetched through the authed /api/ha image proxy (an
  // <img src> can't carry the device-token header, and HA/MA art is HTTP on
  // the LAN so it can't be loaded directly from the HTTPS kiosk). Re-fetched
  // only when the artwork URL actually changes, not on every poll.
  const [artUrl, setArtUrl] = useState<string | null>(null);
  const artKeyRef = useRef<string | null>(null);

  const fetchState = useCallback(async () => {
    try {
      const res = await apiFetch(
        `/api/ha?entities=${encodeURIComponent(KIOSK_PLAYER_ENTITY)}`,
      );
      if (!alive.current) return;
      if (!res.ok) {
        setError(`ha ${res.status}`);
        return;
      }
      const json = (await res.json()) as HaResponse;
      setData(json);
      setError(null);
      const entity = json.states[KIOSK_PLAYER_ENTITY];
      if (entity) {
        const isPlaying = entity.state === 'playing';
        setOptimisticPlaying((prev) => (prev === null || prev === isPlaying ? null : prev));
        // Clear the optimistic volume override once the server snapshot agrees
        // (within 1% to absorb float rounding).
        const vol = Math.round((entity.attributes.volume_level ?? 0) * 100);
        setOptimisticVolume((prev) =>
          prev === null || Math.abs(prev - vol) <= 1 ? null : prev,
        );
        // Once the player is genuinely no longer active, drop the dismiss
        // latch so a later launch shows now-playing again.
        if (!ACTIVE_STATES.includes(entity.state)) setDismissed(false);
      }
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : 'fetch failed');
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void fetchState();
    const stop = pollWithWake(() => void fetchState(), REFRESH_MS);
    return () => {
      alive.current = false;
      stop();
    };
  }, [fetchState]);

  const entity = data?.states[KIOSK_PLAYER_ENTITY];
  const attrs = entity?.attributes;
  const artKey = attrs?.entity_picture ?? attrs?.media_image_url ?? null;
  const active = !!entity && ACTIVE_STATES.includes(entity.state);
  const showNowPlaying = active && !dismissed;

  // Title/artist to display. During a transient metadata gap (player active but
  // HA hasn't populated media_title yet — track change / buffering) show a clear
  // placeholder instead of a blank line.
  const haveTitle = !!attrs?.media_title;
  const displayTitle = attrs?.media_title ?? 'Now playing';
  const displayArtist = haveTitle ? attrs?.media_artist : 'Loading…';

  // Refresh the album art only when the underlying art URL changes.
  useEffect(() => {
    let revoked = false;
    let created: string | null = null;
    if (!active || !artKey) {
      artKeyRef.current = null;
      setArtUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    if (artKey === artKeyRef.current) return;
    artKeyRef.current = artKey;
    void (async () => {
      try {
        const res = await apiFetch(
          `/api/ha?image=${encodeURIComponent(KIOSK_PLAYER_ENTITY)}`,
        );
        if (!res.ok || revoked) return;
        const blob = await res.blob();
        if (revoked) return;
        created = URL.createObjectURL(blob);
        setArtUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return created;
        });
      } catch {
        /* leave the placeholder art in place */
      }
    })();
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [artKey, active]);

  const isPlaying = optimisticPlaying ?? entity?.state === 'playing';

  // Ticking wall-clock that advances the progress ring between the 3s polls.
  // HA only stamps media_position at media_position_updated_at, so while playing
  // we extrapolate from that stamp; ticking every second keeps the donut moving
  // smoothly without hammering the poller. Only runs while playing.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!isPlaying) return;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isPlaying]);

  // Playback fraction (0–1) for the donut ring. Extrapolate the last reported
  // position forward by the time elapsed since HA stamped it (only while
  // playing — a paused position is frozen). Null when the player doesn't report
  // a usable duration (e.g. live radio), so we can hide the ring entirely.
  const duration = attrs?.media_duration ?? 0;
  const basePosition = attrs?.media_position ?? 0;
  let progress: number | null = null;
  if (duration > 0) {
    let elapsed = basePosition;
    if (isPlaying && attrs?.media_position_updated_at) {
      const drift = (nowMs - Date.parse(attrs.media_position_updated_at)) / 1000;
      if (Number.isFinite(drift)) elapsed = basePosition + Math.max(0, drift);
    }
    progress = Math.min(1, Math.max(0, elapsed / duration));
  }

  const call = useCallback(
    async (service: string, extra?: Record<string, unknown>) => {
      setPending(true);
      try {
        const res = await apiFetch('/api/ha', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            domain: 'media_player',
            service,
            entity_id: KIOSK_PLAYER_ENTITY,
            ...extra,
          }),
        });
        if (!res.ok) throw new Error(`ha ${res.status}`);
        setError(null);
        setTimeout(() => void fetchState(), POST_REFRESH_DELAY_MS);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'control failed');
      } finally {
        setPending(false);
      }
    },
    [fetchState],
  );

  function stop() {
    // Optimistically flip back to the launchers, then tell MA to stop.
    setDismissed(true);
    void call('media_stop');
  }

  // Launch the fullscreen youtube.com/tv cast target on the Pi (see
  // api/kiosk/_youtube.ts → scripts/kiosk/youtube.sh). Only does anything on the
  // kiosk host; off-kiosk (dev laptop / Vercel) the endpoint returns 404 and we
  // silently no-op, so the button is harmless everywhere else.
  const openYouTube = useCallback(async () => {
    try {
      await apiFetch('/api/kiosk/youtube', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'open' }),
      });
    } catch {
      // Never surface an error for a video launcher.
    }
  }, []);

  // Current volume (0–100), preferring the optimistic override. `muted` is the
  // client-side flag declared above (this player has no server mute state).
  const volume = optimisticVolume ?? Math.round((attrs?.volume_level ?? 0) * 100);

  // Drag handler for the slider: optimistic immediately, debounced volume_set.
  // Any positive volume means unmuted (we mute by dropping volume to 0).
  const setVolume = useCallback(
    (pct: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(pct)));
      setOptimisticVolume(clamped);
      if (clamped > 0) setMuted(false);
      if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
      volumeTimerRef.current = window.setTimeout(() => {
        void call('volume_set', { volume_level: clamped / 100 });
      }, VOLUME_DEBOUNCE_MS);
    },
    [call],
  );

  // No volume_mute on this player — mute = drop to 0, unmute = restore the
  // remembered pre-mute volume. volume_set is applied immediately (not debounced
  // like the slider) since the tap is a discrete action.
  function toggleMute() {
    if (muted) {
      const restore = preMuteVolRef.current > 0 ? preMuteVolRef.current : 30;
      setMuted(false);
      setOptimisticVolume(restore);
      void call('volume_set', { volume_level: restore / 100 });
    } else {
      preMuteVolRef.current = volume > 0 ? volume : preMuteVolRef.current;
      setMuted(true);
      setOptimisticVolume(0);
      void call('volume_set', { volume_level: 0 });
    }
  }

  // Speaker button gesture: a quick tap toggles mute; a press-and-hold opens
  // the volume popover and — without lifting — keeps adjusting volume as the
  // finger drags up/down, releasing leaves the popover open for fine tweaks.
  // The trailing click after a hold is suppressed so it doesn't also mute.
  function onSpeakerPointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
    heldRef.current = false;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    holdTimerRef.current = window.setTimeout(() => {
      heldRef.current = true;
      setAnchorRect(speakerBtnRef.current?.getBoundingClientRect() ?? null);
      setVolumeOpen(true);
      dragRef.current = { startY, startVol: volume };
      // Keep receiving moves on the button even though the popover backdrop
      // now sits on top of it.
      try {
        speakerBtnRef.current?.setPointerCapture(pointerId);
      } catch {
        /* capture unsupported / pointer already gone — drag still no-ops safely */
      }
    }, VOLUME_HOLD_MS);
  }
  function onSpeakerPointerMove(e: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = drag.startY - e.clientY; // up = louder
    setVolume(drag.startVol + (delta / VOLUME_TRACK_PX) * 100);
  }
  function endSpeakerPress() {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    dragRef.current = null;
  }
  function onSpeakerClick() {
    if (heldRef.current) {
      heldRef.current = false;
      return; // long-press already opened the popover; don't also toggle mute
    }
    toggleMute();
  }

  // After 15 min paused, abandon the session: stop and return to launchers.
  // entity.state stays 'paused' across polls, so this timer isn't reset on
  // every refresh — only when the state actually changes.
  useEffect(() => {
    if (entity?.state !== 'paused') return;
    const t = window.setTimeout(() => {
      setDismissed(true);
      void call('media_stop');
    }, PAUSE_RESET_MS);
    return () => clearTimeout(t);
  }, [entity?.state, call]);

  // Tidy up the gesture/debounce timers on unmount.
  useEffect(
    () => () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
    },
    [],
  );

  const SpeakerIcon = muted
    ? SpeakerSlashIcon
    : volume === 0
      ? SpeakerNoneIcon
      : volume < 50
        ? SpeakerLowIcon
        : SpeakerHighIcon;

  const modal = (
    <MusicBrowserModal
      // Stable key: the now-playing and launcher branches render {modal} at
      // different child positions (the now-playing branch has the VolumePopover
      // slot before it). Without a key, React reconciles by position and
      // unmounts/remounts the modal whenever playback starts or stops — wiping
      // its open browse/detail navigation. The key matches it by identity across
      // the branch switch so it stays mounted.
      key="music-browser-modal"
      open={browserOpen}
      onClose={() => setBrowserOpen(false)}
      playerEntity={KIOSK_PLAYER_ENTITY}
      configEntryId={MA_CONFIG_ENTRY_ID}
      onPlayed={() => {
        setDismissed(false);
        setTimeout(() => void fetchState(), POST_REFRESH_DELAY_MS);
      }}
    />
  );

  // ---- Now-playing (active) ----
  if (showNowPlaying) {
    return (
      <>
        <div
          className={`card !p-3 h-28 relative flex items-center gap-3 overflow-hidden ${className ?? ''}`}
        >
          <div className="relative h-full aspect-square shrink-0">
            {/* Spinning record, inset so the donut progress ring sits in the
                gap around it. */}
            <div
              // Inline anim-play-state beats the unlayered `.record-spin` rule
              // (a Tailwind utility class would lose the cascade to it).
              style={{ animationPlayState: isPlaying ? 'running' : 'paused' }}
              className="absolute inset-[5px] rounded-full bg-subtle border border-border overflow-hidden grid place-items-center record-spin"
            >
              {artUrl ? (
                <img src={artUrl} alt="Album art" className="h-full w-full object-cover" />
              ) : (
                <MusicNotesIcon size={28} weight="fill" className="text-fg-tertiary" />
              )}
              {/* Spindle hole — the small center dot that sells the record look.
                  Centered, so the spin doesn't visibly move it. */}
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-canvas border border-border" />
            </div>

            {/* Donut progress ring — stationary (sibling of the spinning art so
                it doesn't rotate), drawn around the cover art. Hidden when the
                player reports no usable duration (e.g. live radio). -rotate-90
                starts the arc at 12 o'clock; the 1s transition matches the tick
                so the fill advances smoothly between polls. */}
            {progress !== null && (
              <svg
                viewBox="0 0 100 100"
                className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="46"
                  fill="none"
                  strokeWidth="4"
                  className="stroke-border"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="46"
                  fill="none"
                  strokeWidth="4"
                  strokeLinecap="round"
                  className="stroke-green"
                  strokeDasharray={2 * Math.PI * 46}
                  strokeDashoffset={2 * Math.PI * 46 * (1 - progress)}
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>
            )}
          </div>

          <div className="flex-1 min-w-0 h-full relative">
            {/* Title/artist pinned to the top so the transport row below holds a
                fixed position whether or not the artist line is present. pr-9
                keeps them clear of the absolutely-positioned close button. */}
            <div className="absolute inset-x-0 top-0">
              <div className="text-sm font-semibold leading-tight truncate pr-9">
                {displayTitle}
              </div>
              {displayArtist && (
                <div className="text-xs text-fg-secondary leading-tight truncate pr-9">
                  {displayArtist}
                </div>
              )}
            </div>
            {/* Transport pinned to the bottom — fixed position regardless of the
                title/artist height above. */}
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-1">
              <button
                type="button"
                onClick={() => void call('media_previous_track')}
                disabled={pending}
                aria-label="Previous track"
                className="grid place-items-center shrink-0 w-9 h-9 rounded-full bg-elevated border border-border text-fg transition-transform active:scale-[0.94] disabled:opacity-40"
              >
                <SkipBackIcon size={16} weight="fill" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setOptimisticPlaying(!isPlaying);
                  void call('media_play_pause');
                }}
                disabled={pending}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                aria-pressed={isPlaying}
                className="grid place-items-center shrink-0 w-11 h-11 rounded-full bg-elevated border border-border text-white transition-transform active:scale-[0.94] disabled:opacity-40"
              >
                {isPlaying ? (
                  <PauseIcon size={20} weight="fill" />
                ) : (
                  <PlayIcon size={20} weight="fill" />
                )}
              </button>
              <button
                type="button"
                onClick={() => void call('media_next_track')}
                disabled={pending}
                aria-label="Next track"
                className="grid place-items-center shrink-0 w-9 h-9 rounded-full bg-elevated border border-border text-fg transition-transform active:scale-[0.94] disabled:opacity-40"
              >
                <SkipForwardIcon size={16} weight="fill" />
              </button>
              <button
                type="button"
                onClick={() => setBrowserOpen(true)}
                aria-label="Browse music"
                className="grid place-items-center shrink-0 w-9 h-9 rounded-full bg-elevated border border-border text-fg transition-transform active:scale-[0.94]"
              >
                <MagnifyingGlassIcon size={16} weight="bold" />
              </button>
              {/* Tap = mute toggle, long-press = volume slider popover. */}
              <button
                ref={speakerBtnRef}
                type="button"
                onClick={onSpeakerClick}
                onPointerDown={onSpeakerPointerDown}
                onPointerMove={onSpeakerPointerMove}
                onPointerUp={endSpeakerPress}
                onPointerCancel={endSpeakerPress}
                aria-label={muted ? 'Unmute' : 'Mute'}
                aria-pressed={muted}
                className={`ml-auto grid place-items-center shrink-0 w-9 h-9 rounded-full border border-border transition-transform active:scale-[0.94] touch-none ${
                  muted ? 'bg-subtle text-fg-tertiary' : 'bg-elevated text-fg'
                }`}
              >
                <SpeakerIcon size={16} weight="fill" />
              </button>
            </div>
          </div>

          {/* Close / stop — returns to the launchers */}
          <button
            type="button"
            onClick={stop}
            aria-label="Stop and close"
            // right-3 + w-9 matches the speaker button's column (text block sits
            // at p-3) so the X and speaker line up vertically on the right edge.
            className="absolute top-3 right-3 grid place-items-center shrink-0 w-9 h-9 rounded-full bg-elevated border border-border text-fg-secondary transition-transform active:scale-[0.94]"
          >
            <XIcon size={16} weight="bold" />
          </button>

          {error && (
            <span className="absolute bottom-1 right-2 text-[0.625rem] text-amber">
              {error}
            </span>
          )}
        </div>
        {volumeOpen && anchorRect && (
          <VolumePopover
            anchor={anchorRect}
            value={volume}
            onChange={setVolume}
            onClose={() => setVolumeOpen(false)}
          />
        )}
        {modal}
      </>
    );
  }

  // ---- Launchers (idle) ----
  // h-28 matches the slot the launcher buttons have always occupied; the
  // now-playing card above sizes to its own content (taller), and the clock
  // (flex-1) absorbs the difference. Spotify opens the picker modal; YouTube
  // launches the fullscreen youtube.com/tv cast target on the Pi.
  return (
    <>
      <div className={`h-28 flex gap-6 ${className ?? ''}`}>
        <button
          type="button"
          aria-label="YouTube"
          onClick={openYouTube}
          className="flex-1 card flex flex-col items-center justify-center gap-3 text-white cursor-pointer transition-transform active:scale-[0.97]"
        >
          <YouTubeLogo size={40} />
        </button>
        <button
          type="button"
          aria-label="Spotify"
          onClick={() => setBrowserOpen(true)}
          className="flex-1 card flex flex-col items-center justify-center gap-3 text-white cursor-pointer transition-transform active:scale-[0.97]"
        >
          <SpotifyLogo size={40} />
        </button>
      </div>
      {modal}
    </>
  );
}

interface VolumePopoverProps {
  anchor: DOMRect;
  value: number; // 0–100
  onChange: (pct: number) => void;
  onClose: () => void;
}

// Vertical volume slider rendered in a portal so it floats above the kiosk
// grid (the now-playing card is overflow-hidden and would otherwise clip it)
// without reflowing any other card. Positioned to rise upward from the speaker
// button; tapping the full-screen backdrop dismisses it, and it also
// auto-dismisses 5s after the last volume change.
function VolumePopover({ anchor, value, onChange, onClose }: VolumePopoverProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  // Auto-dismiss after 5s of inactivity. Keyed on `value` so each adjustment
  // (from the slider or the speaker button's hold-drag) restarts the countdown;
  // a ref holds the latest onClose so the parent re-rendering on every poll
  // doesn't reset the timer.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const t = window.setTimeout(() => onCloseRef.current(), 5000);
    return () => clearTimeout(t);
  }, [value]);

  const setFromClientY = useCallback(
    (clientY: number) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Top of the track = 100%, bottom = 0%.
      const pct = (1 - (clientY - rect.top) / rect.height) * 100;
      onChange(pct);
    },
    [onChange],
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[60]"
      onPointerDown={onClose}
      role="presentation"
    >
      <div
        className="absolute"
        style={{
          left: anchor.left + anchor.width / 2,
          top: anchor.top - 8,
          transform: 'translate(-50%, -100%)',
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="card !p-3 flex flex-col items-center gap-2">
          <span className="text-xs font-semibold tabular text-fg leading-none">
            {Math.round(value)}
          </span>
          <div
            ref={trackRef}
            className="relative w-3 h-36 rounded-full bg-canvas border border-border touch-none cursor-pointer"
            onPointerDown={(e) => {
              draggingRef.current = true;
              trackRef.current?.setPointerCapture(e.pointerId);
              setFromClientY(e.clientY);
            }}
            onPointerMove={(e) => {
              if (draggingRef.current) setFromClientY(e.clientY);
            }}
            onPointerUp={() => {
              draggingRef.current = false;
            }}
            onPointerCancel={() => {
              draggingRef.current = false;
            }}
          >
            {/* Filled portion grows from the bottom. */}
            <div
              className="absolute inset-x-0 bottom-0 rounded-full bg-green"
              style={{ height: `${value}%` }}
            />
            {/* Thumb centered on the current value — light fill + dark ring +
                shadow so it stays legible over both the fill and the track. */}
            <div
              className="absolute left-1/2 w-6 h-6 -translate-x-1/2 translate-y-1/2 rounded-full bg-fg border-2 border-canvas shadow-lg"
              style={{ bottom: `${value}%` }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
